import axios from 'axios';
import { useState, useEffect } from 'react';

const BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const LS_TOKEN = 'bigtime_jwt_v1';
const LS_USER = 'bigtime_user_v1';

export const api = axios.create({ baseURL: BASE, withCredentials: true });

export function getToken() {
  return localStorage.getItem(LS_TOKEN) || '';
}

api.interceptors.request.use((cfg) => {
  // iter36 · Token is now sent via httpOnly cookie (withCredentials: true).
  // We keep the Authorization header as backup so requests from other tabs
  // that haven't refreshed the auth service yet still authenticate.
  const t = localStorage.getItem(LS_TOKEN);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Images saved with an absolute host (e.g. the preview domain) break on other domains: make them relative.
const UPLOAD_RE = /^https?:\/\/[^/]+(\/api\/uploads\/.+)$/;
const relativizeUploads = (v) => {
  if (typeof v === 'string') { const m = v.match(UPLOAD_RE); return m ? m[1] : v; }
  if (Array.isArray(v)) return v.map(relativizeUploads);
  if (v && typeof v === 'object') { for (const k of Object.keys(v)) v[k] = relativizeUploads(v[k]); }
  return v;
};

api.interceptors.response.use((r) => { if (r.data && typeof r.data === 'object') relativizeUploads(r.data); return r; }, (err) => {
  if (err.response?.status === 401) {
    // token expired
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_USER);
    window.dispatchEvent(new CustomEvent('bigtime-auth-change'));
  }
  return Promise.reject(err);
});

export async function login(role, username, password) {
  // Cookie is set server-side; we still cache the token in localStorage as a
  // hot backup so pages that load before the cookie is applied stay auth'd.
  const { data } = await api.post('/auth/login', { role, username, password });
  localStorage.setItem(LS_TOKEN, data.access_token);
  localStorage.setItem(LS_USER, JSON.stringify(data.user));
  window.dispatchEvent(new CustomEvent('bigtime-auth-change'));
  return data.user;
}

export function logout() {
  // Best-effort: ask the server to clear the cookie. Local storage is cleared
  // regardless so the UI reflects logout immediately.
  api.post('/auth/logout', {}).catch(() => {});
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_USER);
  window.dispatchEvent(new CustomEvent('bigtime-auth-change'));
}

export function currentUser() {
  try { return JSON.parse(localStorage.getItem(LS_USER) || 'null'); } catch { return null; }
}

/** Fetch the fresh user profile from /auth/me (includes effective_permissions).
 *  Silently no-ops if unauthenticated. Updates localStorage + broadcasts. */
export async function refreshCurrentUser() {
  if (!localStorage.getItem(LS_TOKEN)) return null;
  try {
    const { data } = await api.get('/auth/me');
    localStorage.setItem(LS_USER, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('bigtime-auth-change'));
    return data;
  } catch { return null; }
}

export function useAuth() {
  const [u, setU] = useState(currentUser());
  useEffect(() => {
    const h = () => setU(currentUser());
    window.addEventListener('bigtime-auth-change', h);
    // Refresh once on mount so we pick up server-side permission changes
    refreshCurrentUser();
    return () => window.removeEventListener('bigtime-auth-change', h);
  }, []);
  return { user: u, refresh: refreshCurrentUser };
}
